'use client';

import { FormEvent } from 'react';
import { Box, Modal } from '@wishket/design-system';
import { useRouter, usePathname } from 'next/navigation';

import useProfileInput from './useProfileInput';
import { CAREER_MAP } from './Profile.constants';

import { ProfileInfoForm } from '@/features/Profile';
import {
  useLevelQuery,
  useProfileInfoMutation,
  useProfileInfoQuery,
  useRoleQuery,
} from '@/entities/profile/models';
import { useDeviceCheck, useProfile } from '@/shared/hooks';
import { UIOverlayModalProps } from '@/shared/types';
import { ClientAuth } from '@/shared/auth';
import { PATH } from '@/shared/constants';

interface ProfileEditProps {
  onClose: () => void;
}

const ProfileEdit = ({ onClose }: ProfileEditProps) => {
  const { profile } = useProfile();
  const router = useRouter();
  const pathname = usePathname();
  const { isLoading: isProfileInfoLoading, data: profileInfo } =
    useProfileInfoQuery({
      yozmitProfileId: profile?.yozmitProfileId,
    });
  const { data, action, keyValue, error, validate } = useProfileInput({
    profileInfo,
  });
  const { mutate } = useProfileInfoMutation();

  const { data: roleData } = useRoleQuery();
  const { data: levelData } = useLevelQuery();

  if (!profile?.yozmitProfileId || isProfileInfoLoading) return;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const accessToken = await ClientAuth.getAccessToken();

    if (!accessToken) {
      router.push(`${PATH.AUTH_LOGIN}?next=${pathname}`);
      return;
    }

    if (!validate() || !profile.yozmitProfileId) return;

    mutate({
      role: data.role,
      level: data.level,
      career: data.career,
      addedSkills: data.skill.addedSkills,
      removedSkills: data.skill.removedSkills,
      yozmitProfileId: profile.yozmitProfileId,
    });
    onClose();
  };

  return (
    <Box className="w-full overflow-visible">
      <ProfileInfoForm
        selectedRole={keyValue(data.role, roleData?.constants || {})}
        handleRoleChange={action.role}
        roleData={roleData}
        skillData={data.skill}
        setSearchStackValue={action.skill.setSkillQuery}
        deleteStackChip={action.skill.handleSkillDelete}
        skillInput={action.skill.handleSkillInput}
        selectedCareer={keyValue(data.career, CAREER_MAP)}
        handleCareerChange={action.career}
        levelData={levelData?.levelData || []}
        selectedLevel={keyValue(data.level, levelData?.constants || {})}
        handleLevelChange={action.level}
        error={error}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </Box>
  );
};

interface ProfileEditModalProps extends UIOverlayModalProps {
  onClose: () => void;
}

const ProfileEditModal = ({
  isOpen,
  onClose,
  rootId,
}: ProfileEditModalProps) => {
  const { isDesktop } = useDeviceCheck();

  return (
    <Modal
      rootId={rootId}
      isOpen={isOpen}
      onClose={onClose}
      variant={isDesktop ? 'default' : 'full'}
      isPreventScroll
    >
      <ProfileEdit onClose={onClose} />
    </Modal>
  );
};

export default ProfileEditModal;
